import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminService } from '../services/api';
import Navigation from '../components/Navigation';
import LoadingSpinner from '../components/LoadingSpinner';
import HIPAAWarningModal from '../components/HIPAAWarningModal';
import { Badge, Button, Card, Input } from '../components/ui';

const LICENSE_TYPE_OPTIONS = [
  'LPC',
  'LPC-S',
  'LCSW',
  'LMFT',
  'LMFT-S',
  'LCDC',
  'PsyD',
  'Psychiatrist',
  'Psychologist',
  'MD',
  'PA',
  'NP',
  'Life Coach'
];

const formatDateInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const toIsoDate = (value) => (value ? new Date(value).toISOString() : '');

const buildCredentialForm = (user) => ({
  licenseNumber: user?.licenseNumber || '',
  licenseType: user?.licenseType || '',
  licenseState: user?.licenseState || '',
  licenseExpiration: formatDateInput(user?.licenseExpiration),
  city: user?.city || '',
  state: user?.state || '',
  ordinationChurch: user?.ordinationChurch || '',
  ordinationYear: user?.ordinationYear || '',
  licenses: (user?.licenses || []).map((license) => ({
    licenseNumber: license?.licenseNumber || '',
    licenseType: license?.licenseType || '',
    licenseState: license?.licenseState || '',
    licenseExpiration: formatDateInput(license?.licenseExpiration)
  }))
});

const buildCredentialSummary = (user) => {
  if (!user) return '';
  const header = [
    `User ID: ${user._id || 'n/a'}`,
    `Email: ${user.email || 'n/a'}`,
    `Role: ${user.role || 'n/a'}`
  ].join('\n');
  const primary = [user.licenseType, user.licenseNumber, user.licenseState]
    .filter(Boolean)
    .join(' | ');
  const expiration = user.licenseExpiration
    ? `Expires: ${new Date(user.licenseExpiration).toLocaleDateString()}`
    : 'Expires: not provided';
  const location = [user.city, user.state].filter(Boolean).join(', ') || 'Location: not provided';
  const ordination = user.role === 'clergy'
    ? `Ordination: ${user.ordinationChurch || 'not provided'}${user.ordinationYear ? ` (${user.ordinationYear})` : ''}`
    : 'Ordination: n/a';
  const additionalLicenses = (user.licenses || [])
    .map((license) => license.licenseType)
    .filter(Boolean);
  const additional = additionalLicenses.length
    ? `Additional Licenses: ${additionalLicenses.join(', ')}`
    : 'Additional Licenses: none';

  return [
    header,
    `Primary License: ${primary || 'not provided'}`,
    expiration,
    location,
    ordination,
    additional
  ].join('\n');
};

const UserDetailsPage = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showHIPAAWarning, setShowHIPAAWarning] = useState(false);
  const [accessForm, setAccessForm] = useState({
    role: '',
    planTier: 'basic',
    clientTier: 'foundation'
  });
  const [accessSaving, setAccessSaving] = useState(false);
  const [subscriptionForm, setSubscriptionForm] = useState({
    plan: 'none',
    status: 'none',
    billingCycle: 'none',
    clientSlots: 0
  });
  const [subscriptionSaving, setSubscriptionSaving] = useState(false);
  const [credentialForm, setCredentialForm] = useState(null);
  const [credentialMessage, setCredentialMessage] = useState(null);
  const [credentialSaving, setCredentialSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  const fetchUserDetails = useCallback(async () => {
    const response = await adminService.getUserById(userId);
    const nextUser = response.data?.data?.user;

    if (!nextUser) {
      setUser(null);
      setMessage({ type: 'error', text: 'User not found.' });
      return;
    }

    setUser(nextUser);
    setMessage(null);
  }, [userId]);

  useEffect(() => {
    const loadUserDetails = async () => {
      setLoading(true);
      try {
        await fetchUserDetails();
      } catch (error) {
        setMessage({
          type: 'error',
          text: error.response?.data?.message || 'Failed to load user details.'
        });
      } finally {
        setLoading(false);
      }
    };

    loadUserDetails();
  }, [fetchUserDetails]);

  useEffect(() => {
    if (!user) return;
    setAccessForm({
      role: user.role || 'client',
      planTier: user.planTier || 'basic',
      clientTier: user.clientTier || 'foundation'
    });
    setSubscriptionForm({
      plan: user.subscription?.plan || 'none',
      status: user.subscription?.status || 'none',
      billingCycle: user.subscription?.billingCycle || 'none',
      clientSlots: user.subscription?.clientSlots || 0
    });
    setCredentialForm(buildCredentialForm(user));
  }, [user]);

  const handleViewJournal = () => {
    setShowHIPAAWarning(true);
  };

  const handleAcceptHIPAA = () => {
    setShowHIPAAWarning(false);
    navigate(`/admin/users/${userId}/journal`);
  };

  const reloadUserDetails = async () => {
    setLoading(true);
    try {
      await fetchUserDetails();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load user details.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action, hard = false) => {
    if (!user) return;

    const confirmText = {
      block: 'Block this user?',
      unblock: 'Unblock this user?',
      suspend: 'Suspend this user?',
      reactivate: 'Reactivate this user?',
      verify: 'Verify this user?',
      unverify: 'Unverify this user?',
      graduate: 'Graduate this user?',
      delete: 'Delete this user?',
      restore: 'Restore this user?'
    };

    if (!window.confirm(confirmText[action] || 'Perform this action?')) {
      return;
    }

    setMessage(null);
    setActionLoading(true);

    try {
      switch (action) {
        case 'block':
          await adminService.blockUser(user._id);
          break;
        case 'unblock':
          await adminService.unblockUser(user._id);
          break;
        case 'suspend':
          await adminService.suspendUser(user._id);
          break;
        case 'reactivate':
          await adminService.reactivateUser(user._id);
          break;
        case 'verify':
          await adminService.verifyUser(user._id);
          break;
        case 'unverify':
          await adminService.unverifyUser(user._id);
          break;
        case 'graduate':
          await adminService.graduateUser(user._id);
          break;
        case 'restore':
          await adminService.restoreUser(user._id);
          break;
        case 'delete':
          await adminService.deleteUser(user._id, hard);
          break;
        default:
          break;
      }

      setMessage({ type: 'success', text: 'Action completed successfully.' });
      await reloadUserDetails();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Action failed.'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleAccessChange = (field, value) => {
    setAccessForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveAccess = async () => {
    if (!user) return;
    if (!window.confirm('Update this user\'s role or tier access?')) {
      return;
    }

    setMessage(null);
    setAccessSaving(true);

    try {
      const payload = {
        role: accessForm.role
      };

      if (accessForm.role === 'clinician' || accessForm.role === 'clergy') {
        payload.planTier = accessForm.planTier;
      }

      if (accessForm.role === 'client') {
        payload.clientTier = accessForm.clientTier;
      }

      await adminService.updateUserAccess(user._id, payload);
      setMessage({ type: 'success', text: 'Access updated successfully.' });
      await reloadUserDetails();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update access.'
      });
    } finally {
      setAccessSaving(false);
    }
  };

  const handleSubscriptionChange = (field, value) => {
    setSubscriptionForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSubscription = async () => {
    if (!user) return;
    if (!window.confirm('Update this user\'s subscription tier? A notification will be sent.')) {
      return;
    }

    setMessage(null);
    setSubscriptionSaving(true);

    try {
      const payload = {};
      
      if (subscriptionForm.plan !== (user.subscription?.plan || 'none')) {
        payload.plan = subscriptionForm.plan;
      }
      if (subscriptionForm.status !== (user.subscription?.status || 'none')) {
        payload.status = subscriptionForm.status;
      }
      if (subscriptionForm.billingCycle !== (user.subscription?.billingCycle || 'none')) {
        payload.billingCycle = subscriptionForm.billingCycle;
      }
      if (subscriptionForm.clientSlots !== (user.subscription?.clientSlots || 0)) {
        payload.clientSlots = subscriptionForm.clientSlots;
      }

      if (Object.keys(payload).length === 0) {
        setMessage({ type: 'warning', text: 'No subscription changes to save.' });
        setSubscriptionSaving(false);
        return;
      }

      await adminService.updateUserSubscription(user._id, payload);
      setMessage({ type: 'success', text: 'Subscription updated and notification sent.' });
      await reloadUserDetails();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update subscription.'
      });
    } finally {
      setSubscriptionSaving(false);
    }
  };

  const handleCredentialChange = (field, value) => {
    setCredentialForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAdditionalLicenseChange = (index, field, value) => {
    setCredentialForm((prev) => {
      const updatedLicenses = [...prev.licenses];
      updatedLicenses[index] = {
        ...updatedLicenses[index],
        [field]: value
      };
      return {
        ...prev,
        licenses: updatedLicenses
      };
    });
  };

  const handleAddLicense = () => {
    setCredentialForm((prev) => ({
      ...prev,
      licenses: [
        ...prev.licenses,
        {
          licenseNumber: '',
          licenseType: '',
          licenseState: '',
          licenseExpiration: ''
        }
      ]
    }));
  };

  const handleRemoveLicense = (index) => {
    setCredentialForm((prev) => ({
      ...prev,
      licenses: prev.licenses.filter((_, licenseIndex) => licenseIndex !== index)
    }));
  };

  const handleSaveCredentials = async () => {
    if (!user || !credentialForm) return;
    setCredentialMessage(null);

    const hasPartialLicense = credentialForm.licenses.some((license) => {
      const fields = [license.licenseType, license.licenseNumber, license.licenseState, license.licenseExpiration];
      const filled = fields.filter(Boolean).length;
      return filled > 0 && filled < fields.length;
    });

    if (hasPartialLicense) {
      setCredentialMessage({
        type: 'error',
        text: 'Complete all fields for each additional license or remove the entry.'
      });
      return;
    }

    setCredentialSaving(true);
    try {
      const payload = {
        licenseNumber: credentialForm.licenseNumber.trim(),
        licenseType: credentialForm.licenseType,
        licenseState: credentialForm.licenseState.trim().toUpperCase(),
        licenseExpiration: toIsoDate(credentialForm.licenseExpiration),
        city: credentialForm.city.trim(),
        state: credentialForm.state.trim().toUpperCase(),
        ordinationChurch: credentialForm.ordinationChurch.trim(),
        ordinationYear: credentialForm.ordinationYear ? Number(credentialForm.ordinationYear) : null,
        licenses: credentialForm.licenses
          .filter((license) => license.licenseType && license.licenseNumber && license.licenseState && license.licenseExpiration)
          .map((license) => ({
            licenseNumber: license.licenseNumber.trim(),
            licenseType: license.licenseType,
            licenseState: license.licenseState.trim().toUpperCase(),
            licenseExpiration: toIsoDate(license.licenseExpiration)
          }))
      };

      await adminService.updateUserProfile(user._id, payload);
      setCredentialMessage({ type: 'success', text: 'Credentials updated successfully.' });
      await reloadUserDetails();
    } catch (error) {
      setCredentialMessage({
        type: 'error',
        text: error.response?.data?.message || 'Unable to update credentials.'
      });
    } finally {
      setCredentialSaving(false);
    }
  };

  const showToast = (payload) => {
    setToastMessage(payload);
  };

  const handleCopyCredentials = async () => {
    const summary = buildCredentialSummary(user);
    if (!summary) return;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(summary);
        showToast({ type: 'success', text: 'Credentials summary copied to clipboard.' });
        return;
      }
      showToast({ type: 'error', text: 'Clipboard not available in this browser.' });
    } catch (error) {
      showToast({ type: 'error', text: 'Unable to copy credentials summary.' });
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading user details..." />;
  }

  if (!user) {
    return (
      <>
        <Navigation />
        <main className="mx-auto max-w-[1920px] px-6 py-10">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/admin')}
            className="mb-4"
          >
            ← Back to Dashboard
          </Button>
          <Card>
            <p className="text-center text-neutral-500">User not found.</p>
          </Card>
        </main>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <HIPAAWarningModal
        isOpen={showHIPAAWarning}
        onClose={() => setShowHIPAAWarning(false)}
        onAccept={handleAcceptHIPAA}
        userName={user ? `${user.firstName} ${user.lastName}` : ''}
        userRole="admin"
      />
      {toastMessage && (
        <div
          className="fixed bottom-6 right-6 z-50 max-w-sm"
          onMouseLeave={() => setToastMessage(null)}
        >
          <div
            className={`rounded-soft border px-4 py-3 text-sm shadow-lg ${
              toastMessage.type === 'success'
                ? 'border-success-200 bg-success-50 text-success-700'
                : 'border-error-200 bg-error-50 text-error-700'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <span>{toastMessage.text}</span>
              <button
                type="button"
                className="text-xs font-semibold text-neutral-600 hover:text-neutral-900"
                onClick={() => setToastMessage(null)}
                aria-label="Close notification"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <main className="mx-auto max-w-[1920px] px-6 py-10">
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/admin')}
            className="mb-4"
          >
            ← Back to Dashboard
          </Button>
          <Badge variant="primary" size="sm">Account Review</Badge>
          <h2 className="mt-3 text-3xl font-display font-semibold text-neutral-900">
            {user.firstName} {user.lastName}
          </h2>
          <p className="mt-2 text-neutral-600">Complete account information and technical oversight.</p>
        </div>

        {message && (
          <div
            className={`mb-6 rounded-soft border p-3 text-sm ${
              message.type === 'success'
                ? 'border-success-200 bg-success-50 text-success-700'
                : 'border-error-200 bg-error-50 text-error-700'
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Account Status Overview */}
        <Card className="mb-6">
          <h3 className="text-lg font-display font-semibold text-neutral-800 mb-4">Account Status</h3>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <p className="text-xs font-medium text-neutral-500 uppercase">Status</p>
              <p className="mt-1 text-lg font-semibold text-neutral-800">
                {user.accountStatus === 'inactive' && !user.deletedAt ? 'pending approval' : (user.accountStatus || 'Active')}
              </p>
              <Badge variant={user.accountStatus === 'inactive' && !user.deletedAt ? 'warning' : (user.isActive ? 'success' : 'neutral')} size="sm" className="mt-2">
                {user.accountStatus === 'inactive' && !user.deletedAt ? 'Pending' : (user.isActive ? 'Active' : 'Inactive')}
              </Badge>
            </div>
            <div>
              <p className="text-xs font-medium text-neutral-500 uppercase">Verified</p>
              <p className="mt-1 text-lg font-semibold text-neutral-800">{user.isVerified ? 'Yes' : 'No'}</p>
              <Badge variant={user.isVerified ? 'success' : 'neutral'} size="sm" className="mt-2">
                {user.isVerified ? 'Verified' : 'Unverified'}
              </Badge>
            </div>
            <div>
              <p className="text-xs font-medium text-neutral-500 uppercase">Role</p>
              <p className="mt-1 text-lg font-semibold text-neutral-800">{user.role}</p>
              <Badge variant="secondary" size="sm" className="mt-2">
                {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
              </Badge>
            </div>
          </div>

          {(user.role === 'clinician' || user.role === 'clergy') && (
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              <div>
                <p className="text-xs font-medium text-neutral-500 uppercase">Primary License</p>
                <p className="mt-1 text-sm font-semibold text-neutral-800">
                  {user.licenseType || 'Not provided'}
                </p>
                <p className="text-xs text-neutral-500">{user.licenseNumber || 'License number not provided'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-neutral-500 uppercase">License State</p>
                <p className="mt-1 text-sm font-semibold text-neutral-800">
                  {user.licenseState || 'Not provided'}
                </p>
                <p className="text-xs text-neutral-500">
                  {user.licenseExpiration
                    ? `Expires ${new Date(user.licenseExpiration).toLocaleDateString()}`
                    : 'Expiration not provided'}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-neutral-500 uppercase">Ordination</p>
                <p className="mt-1 text-sm font-semibold text-neutral-800">
                  {user.role === 'clergy' ? (user.ordinationChurch || 'Not provided') : 'Not applicable'}
                </p>
                <p className="text-xs text-neutral-500">
                  {user.role === 'clergy' && user.ordinationYear
                    ? `Ordained ${user.ordinationYear}`
                    : user.role === 'clergy'
                      ? 'Year not provided'
                      : ''}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-neutral-500 uppercase">Location</p>
                <p className="mt-1 text-sm font-semibold text-neutral-800">
                  {user.city || 'City not provided'}
                </p>
                <p className="text-xs text-neutral-500">
                  {user.state || 'State not provided'}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-neutral-500 uppercase">Additional Licenses</p>
                <p className="mt-1 text-sm font-semibold text-neutral-800">
                  {user.licenses?.length || 0}
                </p>
                <p className="text-xs text-neutral-500">
                  {(user.licenses || [])
                    .map((license) => license.licenseType)
                    .filter(Boolean)
                    .join(', ') || 'No additional licenses'}
                </p>
                <button
                  type="button"
                  className="mt-2 text-xs font-semibold text-primary-600 hover:text-primary-700"
                  onClick={() => {
                    const section = document.getElementById('admin-credentials');
                    if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  Jump to credentials
                </button>
                <button
                  type="button"
                  className="mt-2 text-xs font-semibold text-primary-600 hover:text-primary-700"
                  onClick={handleCopyCredentials}
                >
                  Copy credentials summary
                </button>
              </div>
            </div>
          )}
        </Card>

        {(user.role === 'clinician' || user.role === 'clergy') && credentialForm && (
          <Card className="mb-6" id="admin-credentials">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-display font-semibold text-neutral-800">Credentials</h3>
                <p className="text-sm text-neutral-600">
                  {user.role === 'clinician'
                    ? 'Review and edit clinician license details.'
                    : 'Review ordination details and any professional licenses.'}
                </p>
              </div>
              <Button
                variant="secondary"
                onClick={handleSaveCredentials}
                disabled={credentialSaving}
              >
                {credentialSaving ? 'Saving...' : 'Save Credentials'}
              </Button>
            </div>

            {credentialMessage && (
              <div
                className={`mt-4 rounded-soft border p-3 text-sm ${
                  credentialMessage.type === 'success'
                    ? 'border-success-200 bg-success-50 text-success-700'
                    : 'border-error-200 bg-error-50 text-error-700'
                }`}
              >
                {credentialMessage.text}
              </div>
            )}

            {user.role === 'clinician' && (
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <Input
                  label="License Number"
                  value={credentialForm.licenseNumber}
                  onChange={(event) => handleCredentialChange('licenseNumber', event.target.value)}
                  fullWidth
                />
                <label className="text-sm text-neutral-700">
                  <span className="mb-2 block font-medium">License Type</span>
                  <select
                    value={credentialForm.licenseType}
                    onChange={(event) => handleCredentialChange('licenseType', event.target.value)}
                    className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  >
                    <option value="">Select license type</option>
                    {LICENSE_TYPE_OPTIONS.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </label>
                <Input
                  label="License State"
                  value={credentialForm.licenseState}
                  onChange={(event) => handleCredentialChange('licenseState', event.target.value)}
                  fullWidth
                />
                <Input
                  label="License Expiration"
                  type="date"
                  value={credentialForm.licenseExpiration}
                  onChange={(event) => handleCredentialChange('licenseExpiration', event.target.value)}
                  fullWidth
                />
              </div>
            )}

            {user.role === 'clergy' && (
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <Input
                  label="Church of Ordination"
                  value={credentialForm.ordinationChurch}
                  onChange={(event) => handleCredentialChange('ordinationChurch', event.target.value)}
                  fullWidth
                />
                <Input
                  label="Year Ordained"
                  type="number"
                  value={credentialForm.ordinationYear}
                  onChange={(event) => handleCredentialChange('ordinationYear', event.target.value)}
                  fullWidth
                />
              </div>
            )}

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <Input
                label="City"
                value={credentialForm.city}
                onChange={(event) => handleCredentialChange('city', event.target.value)}
                fullWidth
              />
              <Input
                label="State (2-letter code)"
                value={credentialForm.state}
                onChange={(event) => handleCredentialChange('state', event.target.value)}
                fullWidth
              />
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between gap-4">
                <h4 className="text-sm font-semibold text-neutral-800">Additional Licenses</h4>
                <Button size="sm" variant="ghost" onClick={handleAddLicense}>Add License</Button>
              </div>
              <div className="mt-3 space-y-4">
                {credentialForm.licenses.length === 0 && (
                  <p className="text-xs text-neutral-500">No additional licenses added.</p>
                )}
                {credentialForm.licenses.map((license, index) => (
                  <div key={`license-${index}`} className="rounded-soft border border-neutral-200 p-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <Input
                        label="License Number"
                        value={license.licenseNumber}
                        onChange={(event) => handleAdditionalLicenseChange(index, 'licenseNumber', event.target.value)}
                        fullWidth
                      />
                      <label className="text-sm text-neutral-700">
                        <span className="mb-2 block font-medium">License Type</span>
                        <select
                          value={license.licenseType}
                          onChange={(event) => handleAdditionalLicenseChange(index, 'licenseType', event.target.value)}
                          className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                        >
                          <option value="">Select license type</option>
                          {LICENSE_TYPE_OPTIONS.map((type) => (
                            <option key={`${type}-${index}`} value={type}>{type}</option>
                          ))}
                        </select>
                      </label>
                      <Input
                        label="License State"
                        value={license.licenseState}
                        onChange={(event) => handleAdditionalLicenseChange(index, 'licenseState', event.target.value)}
                        fullWidth
                      />
                      <Input
                        label="License Expiration"
                        type="date"
                        value={license.licenseExpiration}
                        onChange={(event) => handleAdditionalLicenseChange(index, 'licenseExpiration', event.target.value)}
                        fullWidth
                      />
                    </div>
                    <div className="mt-3 flex justify-end">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveLicense(index)}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        <Card className="mb-6">
          <h3 className="text-lg font-display font-semibold text-neutral-800 mb-4">Access & Tier</h3>
          <div className="grid gap-4 md:grid-cols-3">
            <label className="text-sm text-neutral-700">
              <span className="mb-2 block font-medium">Role</span>
              <select
                value={accessForm.role}
                onChange={(e) => handleAccessChange('role', e.target.value)}
                className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              >
                <option value="client">Client</option>
                <option value="clinician">Clinician</option>
                <option value="clergy">Clergy</option>
                <option value="admin">Admin</option>
              </select>
            </label>

            {(accessForm.role === 'clinician' || accessForm.role === 'clergy') && (
              <label className="text-sm text-neutral-700">
                <span className="mb-2 block font-medium">Clinician Plan Tier</span>
                <select
                  value={accessForm.planTier}
                  onChange={(e) => handleAccessChange('planTier', e.target.value)}
                  className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                >
                  <option value="basic">Basic (5 clients)</option>
                  <option value="pro">Pro (25 clients)</option>
                  <option value="enterprise">Enterprise (Unlimited)</option>
                </select>
              </label>
            )}

            {accessForm.role === 'client' && (
              <label className="text-sm text-neutral-700">
                <span className="mb-2 block font-medium">Client Tier</span>
                <select
                  value={accessForm.clientTier}
                  onChange={(e) => handleAccessChange('clientTier', e.target.value)}
                  className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                >
                  <option value="foundation">Foundation</option>
                  <option value="guided">Guided</option>
                </select>
              </label>
            )}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button size="sm" variant="primary" onClick={handleSaveAccess} disabled={accessSaving}>
              {accessSaving ? 'Saving...' : 'Save Access'}
            </Button>
          </div>
        </Card>

        {/* Subscription Management */}
        <Card className="mb-6">
          <h3 className="text-lg font-display font-semibold text-neutral-800 mb-4">Subscription Management</h3>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <label className="text-sm text-neutral-700">
              <span className="mb-2 block font-medium">Plan</span>
              <select
                value={subscriptionForm.plan}
                onChange={(e) => handleSubscriptionChange('plan', e.target.value)}
                className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              >
                <option value="none">— None (Free)</option>
                <option value="foundation">Foundation ($29/mo)</option>
                <option value="guided">Guided ($19/mo)</option>
                <option value="recovery">Companion ($9/mo)</option>
                <option value="clinician_practitioner">Practitioner (20 slots)</option>
                <option value="clinician_practice">Practice (50 slots)</option>
                <option value="clinician_org">Organization (75 slots)</option>
                <option value="institutional">Institutional</option>
                <option value="legacy_client">Legacy Client (Free)</option>
                <option value="legacy_clinician">Legacy Clinician (Free)</option>
                <option value="legacy_clergy">Legacy Clergy (Free)</option>
                <option value="legacy_coach">Legacy Coach (Free)</option>
                <option value="voucher">Voucher</option>
                <option value="admin">Admin</option>
              </select>
            </label>

            <label className="text-sm text-neutral-700">
              <span className="mb-2 block font-medium">Status</span>
              <select
                value={subscriptionForm.status}
                onChange={(e) => handleSubscriptionChange('status', e.target.value)}
                className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              >
                <option value="none">None</option>
                <option value="active">Active</option>
                <option value="past_due">Past Due</option>
                <option value="canceled">Canceled</option>
                <option value="trialing">Trialing</option>
                <option value="paused">Paused</option>
              </select>
            </label>

            <label className="text-sm text-neutral-700">
              <span className="mb-2 block font-medium">Billing Cycle</span>
              <select
                value={subscriptionForm.billingCycle}
                onChange={(e) => handleSubscriptionChange('billingCycle', e.target.value)}
                className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              >
                <option value="none">None</option>
                <option value="monthly">Monthly</option>
                <option value="annual">Annual</option>
              </select>
            </label>

            <label className="text-sm text-neutral-700">
              <span className="mb-2 block font-medium">Client Slots</span>
              <input
                type="number"
                min="0"
                max="999"
                value={subscriptionForm.clientSlots}
                onChange={(e) => handleSubscriptionChange('clientSlots', parseInt(e.target.value) || 0)}
                className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              />
            </label>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Button size="sm" variant="primary" onClick={handleSaveSubscription} disabled={subscriptionSaving}>
              {subscriptionSaving ? 'Updating...' : 'Update Subscription & Notify'}
            </Button>
          </div>
        </Card>

        {/* Personal Information */}
        <Card className="mb-6">
          <h3 className="text-lg font-display font-semibold text-neutral-800 mb-4">Personal Information</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <p className="text-sm font-medium text-neutral-500">First Name</p>
              <p className="mt-1 text-neutral-800">{user.firstName}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-500">Last Name</p>
              <p className="mt-1 text-neutral-800">{user.lastName}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-500">Email</p>
              <p className="mt-1 text-neutral-800">{user.email}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-500">Username</p>
              <p className="mt-1 text-neutral-800">{user.username}</p>
            </div>
          </div>
        </Card>

        {/* Clinician-Specific Information */}
        {(user.role === 'clinician' || user.role === 'clergy') && (
          <Card className="mb-6">
            <h3 className="text-lg font-display font-semibold text-neutral-800 mb-4">Clinician Information</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-sm font-medium text-neutral-500">License Number</p>
                <p className="mt-1 text-neutral-800">{user.licenseNumber || '—'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-500">License Type</p>
                <p className="mt-1 text-neutral-800">{user.licenseType || '—'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-500">Plan Tier</p>
                <p className="mt-1 text-neutral-800">{user.planTier || 'Basic'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-500">Assigned Clients</p>
                <p className="mt-1 text-neutral-800">{user.clients?.length || 0}</p>
              </div>
            </div>
          </Card>
        )}

        {/* Client-Specific Information */}
        {user.role === 'client' && (
          <Card className="mb-6">
            <h3 className="text-lg font-display font-semibold text-neutral-800 mb-4">Client Information</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-sm font-medium text-neutral-500">Client Tier</p>
                <p className="mt-1 text-neutral-800">{user.clientTier || 'Foundation'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-500">Current Day</p>
                <p className="mt-1 text-neutral-800">{user.currentDay || 1}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-500">Current Phase</p>
                <p className="mt-1 text-neutral-800">{user.currentPhase || 'Foundation'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-500">Graduation Status</p>
                <p className="mt-1 text-neutral-800">{user.graduationStatus || 'Active'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-500">Assigned Clinician</p>
                <p className="mt-1 text-neutral-800">
                  {user.assignedClinician
                    ? `${user.assignedClinician.firstName} ${user.assignedClinician.lastName}`
                    : '—'}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Account Dates */}
        <Card className="mb-6">
          <h3 className="text-lg font-display font-semibold text-neutral-800 mb-4">Account Dates</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <p className="text-sm font-medium text-neutral-500">Created</p>
              <p className="mt-1 text-neutral-800">
                {user.createdAt ? new Date(user.createdAt).toLocaleString() : '—'}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-500">Last Updated</p>
              <p className="mt-1 text-neutral-800">
                {user.updatedAt ? new Date(user.updatedAt).toLocaleString() : '—'}
              </p>
            </div>
          </div>
        </Card>

        {/* Actions */}
        <Card>
          <h3 className="text-lg font-display font-semibold text-neutral-800 mb-4">Account Actions</h3>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="primary" onClick={handleViewJournal}>
              View Journal
            </Button>
            {user.accountStatus === 'deleted' && (
              <Button size="sm" variant="secondary" onClick={() => handleAction('restore')} disabled={actionLoading}>
                Restore
              </Button>
            )}
            {user.accountStatus === 'inactive' && !user.deletedAt && (
              <Button size="sm" variant="secondary" onClick={() => handleAction('reactivate')} disabled={actionLoading}>
                Approve
              </Button>
            )}
            {user.accountStatus === 'blocked' ? (
              <Button size="sm" variant="ghost" onClick={() => handleAction('unblock')} disabled={actionLoading}>
                Unblock
              </Button>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => handleAction('block')} disabled={actionLoading}>
                Block
              </Button>
            )}

            {user.accountStatus === 'suspended' ? (
              <Button size="sm" variant="ghost" onClick={() => handleAction('reactivate')} disabled={actionLoading}>
                Reactivate
              </Button>
            ) : user.accountStatus === 'inactive' && !user.deletedAt ? null : (
              <Button size="sm" variant="ghost" onClick={() => handleAction('suspend')} disabled={actionLoading}>
                Suspend
              </Button>
            )}

            {user.isVerified ? (
              <Button size="sm" variant="ghost" onClick={() => handleAction('unverify')} disabled={actionLoading}>
                Unverify
              </Button>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => handleAction('verify')} disabled={actionLoading}>
                Verify
              </Button>
            )}

            {user.accountStatus !== 'deleted' && (
              <Button size="sm" variant="secondary" onClick={() => handleAction('graduate')} disabled={actionLoading}>
                Graduate
              </Button>
            )}

            {user.accountStatus !== 'deleted' && (
              <div className="flex gap-2 flex-col">
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => {
                      if (window.confirm(`Soft delete ${user.email}?\n\n✓ HIPAA Compliant - Record retained for audit.\n\nThis is the recommended deletion method.`)) {
                        handleAction('delete', false);
                      }
                    }}
                    disabled={actionLoading}
                  >
                    Soft Delete (Recommended)
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      const warnings = [
                        `⚠️ HARD DELETE ${user.email.toUpperCase()}?`,
                        ``,
                        `⚠️ CRITICAL HIPAA WARNING ⚠️`,
                        ``,
                        `This action will PERMANENTLY REMOVE:`,
                        `• User account and credentials`,
                        `• All journal entries and progress data`,
                        `• All clinical notes and assessments`,
                        `• Complete activity and treatment history`,
                        `• All associated records without a companion plan`,
                        ``,
                        `⚠️ HIPAA COMPLIANCE VIOLATION:`,
                        `Federal regulations require healthcare records be retained`,
                        `for a minimum of 6 years. This deletion may violate:`,
                        `• HIPAA record retention requirements`,
                        `• State medical record laws`,
                        `• Professional liability protections`,
                        ``,
                        user.role === 'admin' ? `⚠️ DELETING ADMIN ACCOUNT: ${user.email}\n` : '',
                        `This action CANNOT be undone and will be logged.`,
                        ``,
                        `Type DELETE to confirm:`
                      ].join('\n');
                      
                      const userConfirm = window.prompt(warnings);
                      if (userConfirm === 'DELETE') {
                        handleAction('delete', true);
                      } else if (userConfirm !== null) {
                        alert('Confirmation text did not match. Hard delete cancelled.');
                      }
                    }}
                    disabled={actionLoading}
                  >
                    ⚠️ Hard Delete (HIPAA Risk)
                  </Button>
                </div>
                {user.role === 'admin' && (
                  <div className="text-xs text-orange-600 font-semibold bg-orange-50 p-2 rounded border border-orange-200">
                    ⚠️ Admin Account: Use extreme caution when deleting
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      </main>
    </>
  );
};

export default UserDetailsPage;
